// ============================================
// ImageEditor.jsx - Editor de Imagem Estilo Lightshot
// Salve este arquivo em: src/components/ImageEditor.jsx
// ============================================

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
    X, Check, Square, Circle, Type, Minus, ArrowRight,
    Pencil, Undo, Redo, RotateCw, Move, ChevronDown
} from 'lucide-react';

const ImageEditor = ({ image, onSave, onCancel }) => {
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const [ctx, setCtx] = useState(null);
    const [isDrawing, setIsDrawing] = useState(false);
    const [tool, setTool] = useState('pencil');
    const [color, setColor] = useState('#FF0000');
    const [lineWidth, setLineWidth] = useState(3);
    const [history, setHistory] = useState([]);
    const [historyStep, setHistoryStep] = useState(-1);
    const [startPos, setStartPos] = useState({ x: 0, y: 0 });
    const [textInput, setTextInput] = useState('');
    const [textPosition, setTextPosition] = useState(null);
    const [showColorPicker, setShowColorPicker] = useState(false);
    const [caption, setCaption] = useState('');
    const [endPos, setEndPos] = useState({ x: 0, y: 0 });
    const [isSending, setIsSending] = useState(false);

    const colors = [
        '#FF0000', '#FF6B00', '#FFE600', '#00FF00', '#00D4FF',
        '#0066FF', '#9D00FF', '#FF00DC', '#000000', '#FFFFFF',
        '#808080', '#C0C0C0'
    ];

    const lineWidths = [2, 3, 5, 8, 12];

    const fitCanvasToContainer = useCallback(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container || !canvas.width || !canvas.height) return;

        const availWidth = Math.max(container.clientWidth - 16, 100);
        const availHeight = Math.max(container.clientHeight - 16, 100);

        let displayW = canvas.width;
        let displayH = canvas.height;

        if (displayW > availWidth) {
            displayH = (availWidth / displayW) * displayH;
            displayW = availWidth;
        }
        if (displayH > availHeight) {
            displayW = (availHeight / displayH) * displayW;
            displayH = availHeight;
        }

        canvas.style.width = `${Math.floor(displayW)}px`;
        canvas.style.height = `${Math.floor(displayH)}px`;
    }, []);

    // Inicializar canvas
    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const context = canvas.getContext('2d');
        setCtx(context);

        const img = new Image();
        img.onload = () => {
            canvas.width = img.width;
            canvas.height = img.height;
            context.drawImage(img, 0, 0);

            fitCanvasToContainer();

            const initialState = canvas.toDataURL();
            setHistory([initialState]);
            setHistoryStep(0);
        };

        if (typeof image === 'string') {
            img.src = image;
        } else if (image instanceof File || image instanceof Blob) {
            const reader = new FileReader();
            reader.onload = (e) => {
                img.src = e.target.result;
            };
            reader.readAsDataURL(image);
        }
    }, [image, fitCanvasToContainer]);

    useEffect(() => {
        window.addEventListener('resize', fitCanvasToContainer);
        return () => window.removeEventListener('resize', fitCanvasToContainer);
    }, [fitCanvasToContainer]);

    const restoreFromHistory = useCallback((step, callback) => {
        const canvas = canvasRef.current;
        if (!canvas || !ctx || !history[step]) return;

        const img = new Image();
        img.onload = () => {
            ctx.clearRect(0, 0, canvas.width, canvas.height);
            ctx.drawImage(img, 0, 0);
            if (callback) callback();
        };
        img.src = history[step];
    }, [ctx, history]);

    const handleUndo = useCallback(() => {
        if (historyStep > 0) {
            const newStep = historyStep - 1;
            setHistoryStep(newStep);
            restoreFromHistory(newStep);
        }
    }, [historyStep, restoreFromHistory]);

    const handleRedo = useCallback(() => {
        if (historyStep < history.length - 1) {
            const newStep = historyStep + 1;
            setHistoryStep(newStep);
            restoreFromHistory(newStep);
        }
    }, [historyStep, history.length, restoreFromHistory]);

    // Atalhos de teclado
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (textPosition || document.activeElement?.tagName === 'INPUT') return;
            if (e.ctrlKey || e.metaKey) {
                if (e.key === 'z') {
                    e.preventDefault();
                    handleUndo();
                } else if (e.key === 'y') {
                    e.preventDefault();
                    handleRedo();
                }
            } else {
                const key = e.key.toLowerCase();
                const toolMap = {
                    'v': 'select',
                    'p': 'pencil',
                    'l': 'line',
                    'a': 'arrow',
                    'r': 'rectangle',
                    'c': 'circle',
                    't': 'text'
                };
                if (toolMap[key]) {
                    setTool(toolMap[key]);
                }
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [handleUndo, handleRedo, textPosition]);

    const saveToHistory = () => {
        const canvas = canvasRef.current;
        if (!canvas) return;

        const newHistory = history.slice(0, historyStep + 1);
        newHistory.push(canvas.toDataURL());
        setHistory(newHistory);
        setHistoryStep(newHistory.length - 1);
    };

    const handleRotate = () => {
        const canvas = canvasRef.current;
        if (!canvas || !ctx) return;
        const tempCanvas = document.createElement('canvas');
        const tempCtx = tempCanvas.getContext('2d');

        tempCanvas.width = canvas.height;
        tempCanvas.height = canvas.width;

        tempCtx.translate(tempCanvas.width / 2, tempCanvas.height / 2);
        tempCtx.rotate((90 * Math.PI) / 180);
        tempCtx.drawImage(canvas, -canvas.width / 2, -canvas.height / 2);

        canvas.width = tempCanvas.width;
        canvas.height = tempCanvas.height;
        ctx.drawImage(tempCanvas, 0, 0);

        fitCanvasToContainer();
        saveToHistory();
    };

    const getPointerPos = (e) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;

        const touch = (e.touches && e.touches[0]) || (e.changedTouches && e.changedTouches[0]);
        const clientX = touch ? touch.clientX : e.clientX;
        const clientY = touch ? touch.clientY : e.clientY;

        return {
            x: (clientX - rect.left) * scaleX,
            y: (clientY - rect.top) * scaleY
        };
    };

    const drawArrow = (context, fromX, fromY, toX, toY, strokeColor, width) => {
        const headLength = width * 4;
        const angle = Math.atan2(toY - fromY, toX - fromX);

        context.strokeStyle = strokeColor;
        context.fillStyle = strokeColor;
        context.lineWidth = width;
        context.lineCap = 'round';
        context.lineJoin = 'round';

        context.beginPath();
        context.moveTo(fromX, fromY);
        context.lineTo(toX, toY);
        context.stroke();

        context.beginPath();
        context.moveTo(toX, toY);
        context.lineTo(
            toX - headLength * Math.cos(angle - Math.PI / 7),
            toY - headLength * Math.sin(angle - Math.PI / 7)
        );
        context.lineTo(
            toX - headLength * Math.cos(angle + Math.PI / 7),
            toY - headLength * Math.sin(angle + Math.PI / 7)
        );
        context.closePath();
        context.fill();
    };

    const handlePointerDown = (e) => {
        if (!ctx) return;
        if (tool === 'text') {
            const pos = getPointerPos(e);
            setTextPosition(pos);
            return;
        }
        if (tool === 'select') return;

        setIsDrawing(true);
        const pos = getPointerPos(e);
        setStartPos(pos);
        setEndPos(pos);

        if (tool === 'pencil') {
            ctx.beginPath();
            ctx.moveTo(pos.x, pos.y);
            ctx.strokeStyle = color;
            ctx.lineWidth = lineWidth;
            ctx.lineCap = 'round';
            ctx.lineJoin = 'round';
        }
    };

    const handlePointerMove = (e) => {
        if (!isDrawing || !ctx) return;
        if (e.cancelable) e.preventDefault();

        const pos = getPointerPos(e);
        setEndPos(pos);

        if (tool === 'pencil') {
            ctx.lineTo(pos.x, pos.y);
            ctx.stroke();
        } else {
            restoreFromHistory(historyStep, () => {
                ctx.strokeStyle = color;
                ctx.fillStyle = color;
                ctx.lineWidth = lineWidth;
                ctx.lineCap = 'round';
                ctx.lineJoin = 'round';

                if (tool === 'rectangle') {
                    const width = pos.x - startPos.x;
                    const height = pos.y - startPos.y;
                    ctx.strokeRect(startPos.x, startPos.y, width, height);
                } else if (tool === 'circle') {
                    const radius = Math.sqrt(
                        Math.pow(pos.x - startPos.x, 2) +
                        Math.pow(pos.y - startPos.y, 2)
                    );
                    ctx.beginPath();
                    ctx.arc(startPos.x, startPos.y, radius, 0, 2 * Math.PI);
                    ctx.stroke();
                } else if (tool === 'line') {
                    ctx.beginPath();
                    ctx.moveTo(startPos.x, startPos.y);
                    ctx.lineTo(pos.x, pos.y);
                    ctx.stroke();
                } else if (tool === 'arrow') {
                    drawArrow(ctx, startPos.x, startPos.y, pos.x, pos.y, color, lineWidth);
                }
            });
        }
    };

    const handlePointerUp = () => {
        if (!isDrawing || !ctx) return;
        setIsDrawing(false);

        if (tool === 'pencil') {
            ctx.closePath();
            saveToHistory();
        } else if (tool !== 'select') {
            restoreFromHistory(historyStep, () => {
                ctx.strokeStyle = color;
                ctx.fillStyle = color;
                ctx.lineWidth = lineWidth;
                ctx.lineCap = 'round';
                ctx.lineJoin = 'round';

                if (tool === 'rectangle') {
                    const width = endPos.x - startPos.x;
                    const height = endPos.y - startPos.y;
                    ctx.strokeRect(startPos.x, startPos.y, width, height);
                } else if (tool === 'circle') {
                    const radius = Math.sqrt(
                        Math.pow(endPos.x - startPos.x, 2) +
                        Math.pow(endPos.y - startPos.y, 2)
                    );
                    ctx.beginPath();
                    ctx.arc(startPos.x, startPos.y, radius, 0, 2 * Math.PI);
                    ctx.stroke();
                } else if (tool === 'line') {
                    ctx.beginPath();
                    ctx.moveTo(startPos.x, startPos.y);
                    ctx.lineTo(endPos.x, endPos.y);
                    ctx.stroke();
                } else if (tool === 'arrow') {
                    drawArrow(ctx, startPos.x, startPos.y, endPos.x, endPos.y, color, lineWidth);
                }
                saveToHistory();
            });
        }
    };

    const handleAddText = () => {
        if (!textInput || !textPosition || !ctx) return;

        const fontSize = Math.max(lineWidth * 6, 18);
        ctx.font = `bold ${fontSize}px Arial`;
        ctx.fillStyle = color;
        ctx.textBaseline = 'top';

        const metrics = ctx.measureText(textInput);
        const textHeight = fontSize;

        ctx.fillStyle = 'rgba(255, 255, 255, 0.8)';
        ctx.fillRect(
            textPosition.x - 4,
            textPosition.y - 4,
            metrics.width + 8,
            textHeight + 8
        );

        ctx.fillStyle = color;
        ctx.fillText(textInput, textPosition.x, textPosition.y);

        saveToHistory();
        setTextInput('');
        setTextPosition(null);
        setTool('select');
    };

    const handleSave = () => {
        if (isSending) return;
        setIsSending(true);
        const canvas = canvasRef.current;
        canvas.toBlob(async (blob) => {
            const file = new File([blob], 'image-edited.png', { type: 'image/png' });
            try {
                await onSave(file, caption);
            } catch (error) {
                console.error("Erro ao enviar imagem:", error);
            } finally {
                setIsSending(false);
            }
        }, 'image/png', 1.0);
    };

    return (
        <div className="fixed inset-0 z-[100] bg-black/95 flex flex-col w-full max-w-[100vw] h-full max-h-[100dvh] overflow-hidden select-none">
            {/* Barra Superior Responsiva com Quebra de Linha */}
            <div className="bg-gray-900 border-b border-gray-700 p-2 sm:p-3 flex flex-col gap-2 w-full max-w-full shrink-0">
                {/* Linha 1: Título + Botões Cancelar e Enviar sempre visíveis */}
                <div className="flex items-center justify-between gap-2 w-full">
                    <h3 className="text-white font-bold text-sm sm:text-lg truncate">Editor de Imagem</h3>

                    <div className="flex items-center gap-2 shrink-0">
                        <button
                            onClick={onCancel}
                            className="px-3 py-1.5 sm:px-4 sm:py-2 bg-gray-700 text-white text-xs sm:text-sm rounded-lg hover:bg-gray-600 transition-colors flex items-center gap-1.5"
                        >
                            <X className="w-4 h-4 sm:w-5 sm:h-5" />
                            <span>Cancelar</span>
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={isSending}
                            className="px-3 py-1.5 sm:px-4 sm:py-2 bg-blue-600 text-white text-xs sm:text-sm rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1.5 font-semibold disabled:opacity-50 disabled:cursor-not-allowed shadow-md"
                        >
                            {isSending ? (
                                <div className="w-4 h-4 sm:w-5 sm:h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                                <Check className="w-4 h-4 sm:w-5 sm:h-5" />
                            )}
                            <span>{isSending ? 'Enviando...' : 'Enviar'}</span>
                        </button>
                    </div>
                </div>

                {/* Linha 2: Ferramentas de desenho, cor, espessura e histórico com flex-wrap */}
                <div className="flex flex-wrap items-center gap-1.5 sm:gap-2 w-full">
                    <div className="flex flex-wrap items-center gap-1 bg-gray-800 rounded-lg p-1 max-w-full">
                        <button onClick={() => setTool('select')} className={`p-1.5 sm:p-2 rounded transition-colors ${tool === 'select' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`} title="Mover (V)">
                            <Move className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                        <button onClick={() => setTool('pencil')} className={`p-1.5 sm:p-2 rounded transition-colors ${tool === 'pencil' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`} title="Desenhar (P)">
                            <Pencil className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                        <button onClick={() => setTool('line')} className={`p-1.5 sm:p-2 rounded transition-colors ${tool === 'line' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`} title="Linha (L)">
                            <Minus className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                        <button onClick={() => setTool('arrow')} className={`p-1.5 sm:p-2 rounded transition-colors ${tool === 'arrow' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`} title="Seta (A)">
                            <ArrowRight className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                        <button onClick={() => setTool('rectangle')} className={`p-1.5 sm:p-2 rounded transition-colors ${tool === 'rectangle' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`} title="Retângulo (R)">
                            <Square className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                        <button onClick={() => setTool('circle')} className={`p-1.5 sm:p-2 rounded transition-colors ${tool === 'circle' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`} title="Círculo (C)">
                            <Circle className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                        <button onClick={() => setTool('text')} className={`p-1.5 sm:p-2 rounded transition-colors ${tool === 'text' ? 'bg-blue-600 text-white' : 'text-gray-300 hover:bg-gray-700'}`} title="Texto (T)">
                            <Type className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                    </div>

                    <div className="relative">
                        <button onClick={() => setShowColorPicker(!showColorPicker)} className="flex items-center gap-1.5 bg-gray-800 px-2.5 py-1.5 sm:px-3 sm:py-2 rounded-lg hover:bg-gray-700 transition-colors">
                            <div className="w-5 h-5 sm:w-6 sm:h-6 rounded border-2 border-white" style={{ backgroundColor: color }} />
                            <ChevronDown className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-300" />
                        </button>

                        {showColorPicker && (
                            <div className="absolute left-0 top-full mt-2 bg-gray-800 p-2.5 rounded-lg shadow-xl z-30 grid grid-cols-6 gap-2 w-max max-w-[85vw] border border-gray-700">
                                {colors.map((c) => (
                                    <button key={c} onClick={() => { setColor(c); setShowColorPicker(false); }} className={`w-7 h-7 sm:w-8 sm:h-8 rounded border-2 transition-transform hover:scale-110 ${color === c ? 'border-blue-500 ring-2 ring-blue-500' : 'border-gray-600'}`} style={{ backgroundColor: c }} />
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="flex items-center gap-1 bg-gray-800 px-2 py-1 rounded-lg max-w-full">
                        <span className="text-gray-300 text-xs hidden sm:inline mr-1">Espessura:</span>
                        <div className="flex gap-1">
                            {lineWidths.map((width) => (
                                <button key={width} onClick={() => setLineWidth(width)} className={`w-6 h-6 sm:w-8 sm:h-8 rounded flex items-center justify-center transition-colors ${lineWidth === width ? 'bg-blue-600' : 'hover:bg-gray-700'}`}>
                                    <div className="rounded-full bg-white" style={{ width: `${width}px`, height: `${width}px` }} />
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="flex items-center gap-1 bg-gray-800 rounded-lg p-1">
                        <button onClick={handleUndo} disabled={historyStep <= 0} className="p-1.5 sm:p-2 rounded text-gray-300 hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" title="Desfazer (Ctrl+Z)">
                            <Undo className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                        <button onClick={handleRedo} disabled={historyStep >= history.length - 1} className="p-1.5 sm:p-2 rounded text-gray-300 hover:bg-gray-700 disabled:opacity-30 disabled:cursor-not-allowed transition-colors" title="Refazer (Ctrl+Y)">
                            <Redo className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                        <button onClick={handleRotate} className="p-1.5 sm:p-2 rounded text-gray-300 hover:bg-gray-700 transition-colors" title="Rotacionar 90°">
                            <RotateCw className="w-4 h-4 sm:w-5 sm:h-5" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Área Central do Canvas */}
            <div ref={containerRef} className="flex-1 min-h-0 min-w-0 w-full flex items-center justify-center p-2 sm:p-4 overflow-hidden">
                <canvas
                    ref={canvasRef}
                    onMouseDown={handlePointerDown}
                    onMouseMove={handlePointerMove}
                    onMouseUp={handlePointerUp}
                    onMouseLeave={handlePointerUp}
                    onTouchStart={handlePointerDown}
                    onTouchMove={handlePointerMove}
                    onTouchEnd={handlePointerUp}
                    className="max-w-full max-h-full shadow-2xl touch-none"
                    style={{ cursor: tool === 'select' ? 'default' : 'crosshair' }}
                />
            </div>

            {/* Campo de Legenda Inferior */}
            <div className="w-full max-w-3xl mx-auto px-2 pb-2 sm:px-4 sm:pb-4 shrink-0">
                <div className="w-full bg-gray-800 rounded-lg p-2.5 sm:p-4">
                    <label className="block text-white text-xs sm:text-sm font-bold mb-1.5 flex items-center gap-1.5">
                        <Type className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        Legenda / Descrição (Opcional)
                    </label>
                    <input
                        type="text"
                        value={caption}
                        onChange={(e) => setCaption(e.target.value)}
                        placeholder="Digite uma legenda para a imagem..."
                        className="w-full px-3 py-2 sm:px-4 sm:py-2.5 bg-gray-700 text-white text-sm rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none transition-colors"
                    />
                </div>
            </div>

            {textPosition && (
                <div className="fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-40">
                    <div className="bg-gray-800 p-4 sm:p-6 rounded-lg shadow-xl max-w-md w-full">
                        <h4 className="text-white font-bold mb-3">Adicionar Texto</h4>
                        <input
                            type="text"
                            value={textInput}
                            onChange={(e) => setTextInput(e.target.value)}
                            onKeyDown={(e) => e.key === 'Enter' && handleAddText()}
                            placeholder="Digite o texto..."
                            autoFocus
                            className="w-full px-3 py-2 bg-gray-700 text-white rounded-lg border border-gray-600 focus:border-blue-500 focus:outline-none mb-4"
                        />
                        <div className="flex gap-2 justify-end">
                            <button onClick={() => { setTextPosition(null); setTextInput(''); }} className="px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600">Cancelar</button>
                            <button onClick={handleAddText} disabled={!textInput} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">Adicionar</button>
                        </div>
                    </div>
                </div>
            )}

            <div className="hidden lg:block absolute bottom-4 left-4 bg-gray-900/90 text-gray-300 text-xs px-3 py-2 rounded-lg pointer-events-none">
                <div className="font-bold mb-1">Atalhos:</div>
                <div>V - Mover | P - Desenhar | L - Linha | A - Seta | R - Retângulo | C - Círculo | T - Texto</div>
                <div>Ctrl+Z - Desfazer | Ctrl+Y - Refazer</div>
            </div>
        </div>
    );
};

export default ImageEditor;
